import CompanionResource from 'flow/api/companion';
import Actions from 'flow/actions';
import { companionCommand, isConnected } from 'flow/api-clients/top-client';
import {
    answerCall,
    hangupSession,
    holdCall,
    mutePhone,
    sendDTMF,
    startLocalCall,
    transferBlindCall,
    unholdCall,
} from 'flow/actions/phone-actions';
import {
    getChannelNameForCallId,
    getLocalSessionFromCallId,
    getOwnActiveCalls,
} from 'flow/selectors/call-selectors';
import { setCompanionMute } from 'flow/api-clients/top-client';
import {
    addUserPhoneLogEntry,
    clearUseAsCompanion,
    getHandoverToDesktop,
    getUseAsCompanion,
    hasUseAsCompanionOptOut,
    saveHandoverToDesktop,
    saveUseAsCompanion,
} from 'flow/utils/browser-utils';
import {
    generateCompanionNonce,
    isCompanionModeEnabled,
    isCompanionSignallingSurface,
    isDesignatedCompanion,
} from 'flow/companion/companion-utils';
import { selectDesignatedCompanionDevice } from 'flow/selectors/app-selectors';
import {
    selectCompanionKnownLocationStatus,
    selectCompanionLocationReportedAt,
    selectCompanionReportedLocationStatus,
} from 'flow/selectors/companion-selectors';
import {
    getCompanionDevice,
    getCompanionDeviceId,
    loadCompanionDevice,
    renameCompanionDevice,
} from 'flow/companion/companion-device-id';
import { getElectronAPI } from 'flow/utils/electron-api';
import { revealDesktopWindow } from 'flow/companion/companion-window';
import {
    answerCompanionPing,
    handleCompanionPong,
} from 'flow/companion/companion-heartbeat';

/**
 * Designate (or undesignate) this desktop install as the user's companion.
 *
 * The designation itself is account-wide and lives in Home; this is the install's own answer to
 * "and is that me?", kept locally so a command arriving before the config fetch can be matched
 * synchronously. Local storage outlives the login, so the two can disagree - see
 * {@link reconcileUndesignatedAccount} for the case where they do.
 *
 * @param {boolean} enabled
 */
export const setUseAsCompanion = (enabled) => (dispatch) => {
    const useAsCompanion = enabled === true;
    saveUseAsCompanion(useAsCompanion);
    mirrorUseAsCompanionToMainProcess(useAsCompanion);
    addUserPhoneLogEntry(`Use as companion set to ${useAsCompanion}`);
    dispatch({
        type: Actions.APP_USE_AS_COMPANION,
        enabled: useAsCompanion,
    });
    if (useAsCompanion) {
        // The companion again, by the toggle or by syncDesignation stepping this machine up.
        dispatch({ type: Actions.APP_COMPANION_ROLE_LOST, lost: false });
    }
};

/**
 * Copy the choice into the electron-store, which the main process reads at startup - localStorage
 * stays the renderer's own copy but cannot be read before the window is shown.
 *
 * Written on every change and every boot ({@link loadUseAsCompanion}), so it cannot drift and an
 * install predating it picks it up. No-op outside the desktop app.
 *
 * @param {boolean} useAsCompanion
 */
const mirrorUseAsCompanionToMainProcess = (useAsCompanion) => {
    try {
        getElectronAPI()?.saveUseAsCompanion(useAsCompanion);
    } catch (error) {
        // Only matters to the next launch; this one already has the value.
        addUserPhoneLogEntry(`Could not mirror use as companion: ${error}`);
    }
};

/**
 * Stand this install down because the account says it is not the companion, rather than because the
 * user said so.
 *
 * Clears the stored flag instead of writing false, which is the difference between "nobody has
 * turned this on" and "the user turned it off". Only the second may survive a boot: syncDesignation
 * promotes this machine back when its account still designates it, and would have no way to tell a
 * stand-down it performed itself from a preference it must not overrule.
 */
const standDownAsCompanion = () => (dispatch) => {
    clearUseAsCompanion();
    // Clearing local storage does not reach the main process, which would go on starting hidden.
    mirrorUseAsCompanionToMainProcess(false);
    dispatch({
        type: Actions.APP_USE_AS_COMPANION,
        enabled: false,
    });
};

/**
 * Put the persisted designation into state at startup. Without this the setting would read as off
 * on every launch until the user touched the toggle again.
 */
export const loadUseAsCompanion = () => (dispatch) => {
    const useAsCompanion = getUseAsCompanion();
    mirrorUseAsCompanionToMainProcess(useAsCompanion);
    dispatch({
        type: Actions.APP_USE_AS_COMPANION,
        enabled: useAsCompanion,
    });
};

/**
 * Put the persisted handover setting into state at startup.
 *
 * Defaults to on, so a user with a desktop app gets handover without going looking for a setting.
 * Safe as a default because it only ever takes effect when there is a companion to take the call -
 * see canRelayCallToCompanion, which falls back to this client when there is not.
 */
export const loadHandoverToDesktop = () => (dispatch) => {
    dispatch({
        type: Actions.APP_HANDOVER_TO_DESKTOP,
        enabled: getHandoverToDesktop(),
    });
};

/**
 * Turn handover to the desktop app on or off for this surface.
 *
 * Per surface, deliberately: a user with Teams on their work laptop and the web app at home should
 * be able to answer this differently in each, and the answer describes how they are set up at that
 * machine rather than anything about their account.
 *
 * @param {boolean} enabled
 */
export const setHandoverToDesktop = (enabled) => (dispatch) => {
    const handover = enabled === true;
    saveHandoverToDesktop(handover);
    addUserPhoneLogEntry(`Handover to desktop app set to ${handover}`);
    dispatch({
        type: Actions.APP_HANDOVER_TO_DESKTOP,
        enabled: handover,
    });
};

/**
 * Fetch the user's registered desktop installs and put them in state - which machine handover goes
 * to, whether one is there at all, and how recently it was seen.
 *
 * No default is applied here any more. Handover is a setting of its own and defaults to on, so a
 * user with a desktop app gets it without the surface having to notice one exists; and with no
 * desktop app the setting has nothing to act on, because canRelayCallToCompanion keeps the call
 * here. Which machine takes the calls is decided on the machine, not on this surface.
 */
export const fetchCompanionDevices = () => (dispatch) => {
    CompanionResource.getDevices({
        onResult(devices) {
            const list = Array.isArray(devices) ? devices : [];
            dispatch({ type: Actions.RESULT_COMPANION_DEVICES, devices: list });
        },
        onError(error) {
            addUserPhoneLogEntry(`Companion device list failed: ${error}`);
            dispatch({ type: Actions.RESULT_COMPANION_DEVICES, devices: [] });
        },
    });
};

/**
 * Drop a companion designation left behind by whoever was logged in here before.
 *
 * Called at boot with the account's designated device. When the account has one there is nothing to
 * reconcile - syncDesignation has already matched this install against it. When it has none, an
 * install still carrying `useAsCompanion` from a previous login would act as the new user's
 * companion unasked: Home stamps commands with the designated device and there isn't one, so
 * nothing else would stop {@link handleCompanionCallControl} answering their calls and opening
 * their microphone.
 *
 * Only the designation, not the handover setting. Handover is per surface and says nothing about
 * whose account it is - "hand my calls to the desktop app when there is one" is as true for the new
 * user as the old, and with nothing designated it does nothing anyway.
 *
 * Not done by clearing local storage at login instead, though that is where the identity switch is
 * noticed: this is the only place that knows whether the *account* has a companion, and a machine
 * the same user genuinely designated must keep its role across a restart.
 *
 * @param {string|null|undefined} designatedDeviceId the account's designated device, if any
 */
export const reconcileUndesignatedAccount =
    (designatedDeviceId) => (dispatch) => {
        if (designatedDeviceId) {
            return;
        }

        if (getUseAsCompanion()) {
            addUserPhoneLogEntry(
                'No device is designated for this account, standing this install down',
            );
            dispatch(standDownAsCompanion());
        }
    };

/**
 * Fetch the server-owned companion feature switch and put it in state. Called once at startup.
 *
 * Deliberately fails closed: any error leaves `companionEnabled` false, so a client that cannot
 * reach Home behaves exactly as it did before the companion work rather than guessing.
 *
 * Also the boot reconcile against the account that is actually logged in, which is not the same
 * question as which install this is. Both companion settings that live in local storage - this
 * machine acting as the companion, and the surface's call method - describe a relationship between
 * an install and an account, and the install outlives the account: log a colleague into this app
 * and every one of them is still there, now describing somebody else's setup. So an account with
 * no designated device says so, and this clears both rather than inheriting them.
 */
export const fetchCompanionConfig = () => (dispatch) => {
    CompanionResource.getConfig({
        onResult(config) {
            const enabled = config?.enabled === true;
            addUserPhoneLogEntry(`Companion feature switch: ${enabled}`);
            dispatch({ type: Actions.APP_COMPANION_ENABLED, enabled });
            dispatch({
                type: Actions.APP_COMPANION_HEARTBEAT_ENABLED,
                enabled: enabled && config?.heartbeatEnabled === true,
            });
            if (!enabled && getUseAsCompanion()) {
                // Started hidden for a designation the backend does not honour, with no window
                // to say so in. From storage, which is populated before state is.
                revealDesktopWindow('the companion feature is not enabled');
            }
            // Also the moment this install's own id is resolved, so that a command arriving later
            // can be matched against it synchronously.
            loadCompanionDevice().then((device) => {
                dispatch({
                    type: Actions.APP_COMPANION_DEVICE_NAME,
                    name: device?.name ?? null,
                });
                dispatch(
                    syncDesignation(config?.designatedDeviceId, {
                        allowStepUp: false,
                    }),
                );
                if (enabled) {
                    dispatch(
                        reconcileUndesignatedAccount(
                            config?.designatedDeviceId,
                        ),
                    );
                    dispatch(registerCompanionDevice());
                    dispatch(reportKnownCompanionLocationStatus());
                    dispatch(fetchCompanionDevices());
                }
            });
        },
        onClientError(error) {
            addUserPhoneLogEntry(
                `Companion feature switch unavailable, staying disabled: ${error}`,
            );
            dispatch({ type: Actions.APP_COMPANION_ENABLED, enabled: false });
        },
        onError(error) {
            addUserPhoneLogEntry(
                `Companion feature switch lookup failed, staying disabled: ${error}`,
            );
            dispatch({ type: Actions.APP_COMPANION_ENABLED, enabled: false });
        },
    });
};

/**
 * Reset the relayed mute once the user has no calls left. Nothing reports "unmuted" when a call
 * merely ends, so without this the mic button stays lit into the next call.
 *
 * Signalling surface only - on the companion onSessionEndedOrFailed owns it.
 */
export const sweepCompanionMuteState = () => (dispatch, getState) => {
    const state = getState();

    if (!isCompanionSignallingSurface(state)) {
        return;
    }

    const believesMuted =
        state.phone.get('isMuted') || state.phone.get('pendingMute') !== null;
    if (!believesMuted) {
        return;
    }

    if (!getOwnActiveCalls(state).isEmpty()) {
        return;
    }

    addUserPhoneLogEntry(
        'No calls left, clearing the mute this surface was showing',
    );
    dispatch({ type: Actions.PHONE_MUTED, muted: false });
    dispatch({ type: Actions.PHONE_MUTE_PENDING_CLEARED });
};

/**
 * Tell the backend this install exists - id, name, platform, version - which also stamps its
 * lastseen. Called at boot when the feature is on, and again after a rename so the stored row
 * carries the new name.
 */
export const registerCompanionDevice = () => () => {
    const device = getCompanionDevice();
    if (!device) {
        return;
    }

    CompanionResource.registerDevice(
        {
            onResult() {
                addUserPhoneLogEntry(
                    `Registered companion device ${device.deviceId} (${device.name})`,
                );
            },
            onClientError(error) {
                addUserPhoneLogEntry(
                    `Companion device registration rejected: ${error}`,
                );
            },
            onError(error) {
                addUserPhoneLogEntry(
                    `Companion device registration failed: ${error}`,
                );
            },
        },
        device,
    );
};

/**
 * How long a reconnect-driven repeat of an unchanged status has to wait.
 *
 * The repeat itself is load-bearing (see {@link reportKnownCompanionLocationStatus}), but every
 * report costs a broadcast to every surface on the account, and a companion on a flaky network
 * reconnects far more often than its location access changes. This bounds how closely the fan-out
 * can track socket stability without giving up the guarantee - a surface that came up during an
 * outage still learns the status, just within this window rather than instantly.
 */
export const LOCATION_REPORT_REPEAT_INTERVAL_MS = 30000;

const rememberLocationReport = (state, { known, reported, reportedAt }) => ({
    type: Actions.APP_COMPANION_LOCATION_REPORT,
    knownStatus:
        known !== undefined ? known : selectCompanionKnownLocationStatus(state),
    reportedStatus:
        reported !== undefined
            ? reported
            : selectCompanionReportedLocationStatus(state),
    reportedAt:
        reportedAt !== undefined
            ? reportedAt
            : selectCompanionLocationReportedAt(state),
});

/**
 * Tell this account's surfaces whether this machine can obtain a position.
 *
 * Derived from whether a position actually resolved, not from the permission state, because on
 * macOS they disagree: Chromium reports the permission as granted while CoreLocation never answers
 * and the request times out (electron/electron#45290). A surface told "granted" would wait for
 * coordinates that are never coming, and show nothing while calling stayed blocked.
 *
 * Only the designated companion reports. A second install is not the machine the calls go to, and
 * its location access says nothing about whether they can be placed. The status is remembered even
 * when this client is not (yet) the companion, so the report can be made once designation is known
 * - the first position is resolved during boot and can easily beat the companion config round-trip.
 *
 * Nothing here may throw. It is called from the geo location path that call setup awaits, so an
 * error escaping would take SIP registration and every dial down with it - and until the endpoint
 * ships in the generated client, calling it at all would do exactly that.
 */
export const reportCompanionLocationStatus =
    (locationStatus) => (dispatch, getState) => {
        dispatch(rememberLocationReport(getState(), { known: locationStatus }));

        if (!isDesignatedCompanion(getState())) {
            return;
        }

        const device = getCompanionDevice();
        if (
            !device ||
            locationStatus === selectCompanionReportedLocationStatus(getState())
        ) {
            return;
        }

        if (typeof CompanionResource.reportLocationStatus !== 'function') {
            addUserPhoneLogEntry(
                'Companion location status not reported: the API client predates the endpoint',
            );
            return;
        }

        // Recorded before the request, so a second caller in the same tick does not send a
        // duplicate; cleared again below if the request never lands.
        dispatch(
            rememberLocationReport(getState(), {
                reported: locationStatus,
                reportedAt: Date.now(),
            }),
        );

        const forget = () =>
            dispatch(rememberLocationReport(getState(), { reported: null }));

        try {
            CompanionResource.reportLocationStatus(
                {
                    onResult() {
                        addUserPhoneLogEntry(
                            `Reported companion location status ${locationStatus}`,
                        );
                    },
                    onError(error) {
                        forget();
                        addUserPhoneLogEntry(
                            `Companion location status report failed: ${error}`,
                        );
                    },
                },
                { deviceId: device.deviceId, locationStatus },
            );
        } catch (error) {
            forget();
            addUserPhoneLogEntry(
                `Companion location status could not be reported: ${error}`,
            );
        }
    };

/**
 * Report again after the companion role moves, after the feature switch arrives, and on every TOP
 * reconnect.
 *
 * All three are races the first report can lose: the position is resolved during boot, before
 * `GET /companion/config` has said whether this client may act as a companion at all; a machine
 * that becomes the companion later has never reported; and a surface that came up while this
 * companion was disconnected holds no status, which reads as "nothing to complain about" and would
 * let the user dial a call the companion then refuses.
 *
 * So the repeat has to happen even when nothing changed. What it must not do is happen per
 * reconnect: each report is a broadcast to every surface on the account, and a companion flapping
 * on a bad network would turn socket churn into fan-out. Outside the window the repeat is dropped
 * and the next change reports as usual.
 */
export const reportKnownCompanionLocationStatus =
    () => (dispatch, getState) => {
        const known = selectCompanionKnownLocationStatus(getState());

        if (known === null) {
            return;
        }

        const reportedAt = selectCompanionLocationReportedAt(getState());
        const unchanged =
            known === selectCompanionReportedLocationStatus(getState());

        if (
            unchanged &&
            reportedAt !== null &&
            Date.now() - reportedAt < LOCATION_REPORT_REPEAT_INTERVAL_MS
        ) {
            addUserPhoneLogEntry(
                'Companion location status repeat skipped, one went out moments ago',
            );
            return;
        }

        dispatch(rememberLocationReport(getState(), { reported: null }));
        dispatch(reportCompanionLocationStatus(known));
    };

export const setCompanionDeviceName = (name) => (dispatch) =>
    renameCompanionDevice(name).then((device) => {
        if (!device) {
            return null;
        }

        dispatch({
            type: Actions.APP_COMPANION_DEVICE_NAME,
            name: device.name,
        });
        dispatch(registerCompanionDevice());

        return device;
    });

export const claimCompanionRole = () => (dispatch) => {
    const device = getCompanionDevice();
    if (!device) {
        addUserPhoneLogEntry(
            'Cannot act as companion, this client has no device id',
        );
        return;
    }

    CompanionResource.designateDevice(
        {
            onResult() {
                addUserPhoneLogEntry(
                    `Companion role claimed by device ${device.deviceId}`,
                );
                dispatch(setUseAsCompanion(true));
            },
            onClientError(error) {
                addUserPhoneLogEntry(`Companion role claim rejected: ${error}`);
            },
            onError(error) {
                addUserPhoneLogEntry(`Companion role claim failed: ${error}`);
            },
        },
        { deviceId: device.deviceId },
    );
};

/**
 * Turn this install off as the companion, and tell the backend the account no longer has one.
 *
 * The counterpart to claimCompanionRole, and it has to reach the backend for the same reason that
 * does. Designating only ever moves the role, so nothing else clears it: an install that stopped
 * acting while still designated leaves every surface relaying calls to a machine that ignores them,
 * with no error anywhere and no staleness to catch it - the machine is running and registering, it
 * just refuses. Undesignating is the only way to say "nobody".
 *
 * Only when the role is actually ours. Turning the toggle off on a machine that does not hold it is
 * a local preference and nothing more; clearing the designation there would stand down whichever of
 * the user's other machines does hold it.
 *
 * The local flag follows the backend rather than leading it, as in claimCompanionRole: a failed
 * release leaves this install acting as the companion, which is what the account still says, rather
 * than silently splitting the two.
 */
export const releaseCompanionRole = () => (dispatch, getState) => {
    const deviceId = getCompanionDeviceId();
    const designated = selectDesignatedCompanionDevice(getState());

    if (!deviceId || !designated || designated.get('deviceId') !== deviceId) {
        dispatch(setUseAsCompanion(false));
        return;
    }

    CompanionResource.undesignateDevice({
        onResult() {
            addUserPhoneLogEntry(
                `Companion role released by device ${deviceId}, the account now has none`,
            );
            dispatch(setUseAsCompanion(false));
        },
        onClientError(error) {
            addUserPhoneLogEntry(`Companion role release rejected: ${error}`);
        },
        onError(error) {
            addUserPhoneLogEntry(`Companion role release failed: ${error}`);
        },
    });
};

/**
 * Give up the companion role when the user logs out of this install.
 *
 * A logged-out desktop app is not on its former account's push channel any more, so it receives
 * nothing - but the account still names it, so every surface goes on relaying calls to it and the
 * calls simply stop arriving. Nothing reports that back. Releasing is the only thing that says so.
 *
 * Only when the role is ours to give up: on a machine that never held it this is a no-op, and
 * clearing someone else's designation from here would stand down whichever machine does hold it.
 *
 * Returns a promise, and resolves on failure as well as success, because the caller is a logout in
 * progress - a release that cannot be made must not be what stops the user leaving.
 *
 * The local `useAsCompanion` flag is deliberately untouched. Logging out says nothing about whether
 * this machine should take calls; the next boot settles that against whatever the account then
 * designates, which is reconcileUndesignatedAccount's job.
 */
export const releaseCompanionRoleOnLogout = () => (_dispatch, getState) =>
    new Promise((resolve) => {
        const deviceId = getCompanionDeviceId();
        const designated = selectDesignatedCompanionDevice(getState());

        if (
            !deviceId ||
            !designated ||
            designated.get('deviceId') !== deviceId
        ) {
            resolve();
            return;
        }

        CompanionResource.undesignateDevice({
            onResult() {
                addUserPhoneLogEntry(
                    `Logging out, released the companion role held by ${deviceId}`,
                );
                resolve();
            },
            onClientError(error) {
                addUserPhoneLogEntry(
                    `Companion role release on logout rejected: ${error}`,
                );
                resolve();
            },
            onError(error) {
                addUserPhoneLogEntry(
                    `Companion role release on logout failed: ${error}`,
                );
                resolve();
            },
        });
    });

export const syncDesignation =
    (designatedDeviceId, { allowStepUp = true } = {}) =>
    (dispatch, getState) => {
        if (designatedDeviceId === undefined || designatedDeviceId === null) {
            return;
        }

        const deviceId = getCompanionDeviceId();
        if (!deviceId) {
            return;
        }

        const useAsCompanion = getState()?.app?.get('useAsCompanion');

        if (deviceId === designatedDeviceId) {
            if (useAsCompanion) {
                return;
            }
            // At boot, step up unless the user actually said not to. allowStepUp: false used to
            // mean "never", which quietly demoted a machine whose own account still designates it:
            // a stand-down this client performed - a colleague logged in here and back out again -
            // reads identically to a preference, and nothing would have restored the role until
            // the user picked the device a second time, with the radio meanwhile showing it as
            // theirs. The two are only distinguishable because a client-side stand-down clears the
            // key (clearUseAsCompanion) where the user's own choice writes false.
            if (!allowStepUp && hasUseAsCompanionOptOut()) {
                addUserPhoneLogEntry(
                    `Companion role is designated to this device (${deviceId}) but it is opted out locally, leaving it off`,
                );
                return;
            }
            // A push deliberately overrides a local "don't use this machine as a companion" - the
            // one asymmetry in this function, and the reason it is logged rather than silent. The
            // designation is account-wide and this push is the user making it seconds ago, so it
            // outranks a preference this install has been holding since who knows when, where the
            // stored designation a boot reads carries no such evidence of intent. The cost is real
            // and accepted: the
            // machine stepping up may be unattended, so picking a device from a surface can start
            // it auto-answering in an empty room. That is the same thing as picking it, made from
            // somewhere else - and the user can see which machine they named.
            addUserPhoneLogEntry(
                `Companion role assigned to this device (${deviceId}), stepping up${
                    hasUseAsCompanionOptOut()
                        ? ", overriding this install's local opt-out"
                        : ''
                }`,
            );
            dispatch(setUseAsCompanion(true));
            return;
        }

        if (!useAsCompanion) {
            return;
        }

        addUserPhoneLogEntry(
            `Companion role moved to device ${designatedDeviceId}, standing down (this device is ${deviceId})`,
        );
        dispatch(standDownAsCompanion());
        // Here rather than in standDownAsCompanion, which also runs for an account with no
        // companion at all - this banner names another machine.
        dispatch({ type: Actions.APP_COMPANION_ROLE_LOST, lost: true });
        revealDesktopWindow('another device took the companion role');
    };

/**
 * Received as a typed TOP push ({@code type: 'companion_location_status'}) whenever the companion's
 * ability to obtain a position changes.
 *
 * Only the surfaces act on it. The companion usually runs as a tray icon with no window, so it has
 * nowhere to tell the user that location access is off - the surface they are looking at has to say
 * it instead, which is the whole reason this push exists.
 *
 * The reporting device is carried so a status from a machine that is not the designated companion
 * can be ignored: with two installs the other one is not the machine the calls go to, and letting
 * it block calling would refuse calls the real companion could place.
 */
export const handleCompanionLocationStatus = (data) => (dispatch, getState) => {
    const deviceId = data?.deviceId ?? null;
    const designated = selectDesignatedCompanionDevice(getState());

    if (deviceId && designated && designated.get('deviceId') !== deviceId) {
        return;
    }

    dispatch({
        type: Actions.APP_COMPANION_LOCATION_STATUS,
        deviceId,
        locationStatus: data?.locationStatus ?? null,
    });
};

/**
 * Received as a typed TOP push ({@code type: 'companion_designation'}) whenever the account's
 * companion changes - designated from the desktop toggle or a surface's call method, or released
 * altogether. Every client of the account gets it, desktop and surface alike, and they use it
 * differently: a desktop app steps up or stands down, a surface only re-reads which machine its
 * calls now go to.
 *
 * Stepping up on the push is what makes the picker work without a walk to the other machine: the
 * push is a user action happening right now, so it may switch the named machine on. The boot
 * reconcile passes allowStepUp: false, refusing that promotion over an explicit local opt-out,
 * because a stored designation carries no such evidence of intent. Standing down is unconditional
 * on both paths, which is what keeps the window with two active companions closed. Both are belt
 * and braces with the deviceId check in {@link handleCompanionCallControl}: this handles the client
 * that is running and listening, that one handles the client that missed the push.
 *
 * No deviceId means the account has no companion at all (see releaseCompanionRole) - a designation
 * like any other, and the one that has to reach a machine still acting as one.
 *
 * @param {{deviceId?: string}} data the device that now holds the role, if any
 */
export const handleCompanionDesignation = (data) => (dispatch, getState) => {
    const deviceId = data?.deviceId;

    if (deviceId) {
        dispatch(syncDesignation(deviceId));
    } else if (getState()?.app?.get('useAsCompanion')) {
        addUserPhoneLogEntry(
            'Companion role released for this account, standing this install down',
        );
        dispatch(standDownAsCompanion());
    }

    // The status belonged to whichever machine held the role: keeping it would leave a surface
    // blocking calls on the old companion's location access, and the new one has no reason to
    // re-report a status it never changed.
    dispatch({
        type: Actions.APP_COMPANION_LOCATION_STATUS,
        deviceId: null,
        locationStatus: null,
    });
    dispatch(reportKnownCompanionLocationStatus());

    // Both ends of the account need the list again: it is where a surface reads the machine it
    // names under the call method, and where a desktop app reads which other machine currently
    // holds the role.
    if (isCompanionModeEnabled(getState())) {
        dispatch(fetchCompanionDevices());
    }
};

/**
 * De-dupe already-handled commands. A single push may fan out to more than one desktop client
 * or be redelivered, and we must only act once. Bounded so a long-lived session doesn't grow it
 * without limit.
 */
const MAX_REMEMBERED_NONCES = 200;
const handledNonces = new Set();

const alreadyHandled = (nonce) => {
    if (!nonce) {
        return false;
    }
    if (handledNonces.has(nonce)) {
        return true;
    }
    // Evict one at a time rather than emptying the set, so the bound costs the oldest nonce and not
    // all of them: clearing wholesale meant a redelivery arriving just after the two-hundredth
    // command was forgotten and acted on twice, which is the one thing this set exists to stop.
    // Set iterates in insertion order, so the first key is the oldest.
    while (handledNonces.size >= MAX_REMEMBERED_NONCES) {
        handledNonces.delete(handledNonces.values().next().value);
    }
    handledNonces.add(nonce);
    return false;
};

/**
 * Find the local session a call-scoped command applies to.
 *
 * A call does not have one Call-ID. When the account has more than one registration the PBX sends
 * an INVITE per registration and each carries its own Call-ID, so the id the signalling surface
 * knows is the id of *a* leg - and with nothing to tell it which, it sends whichever leg TOP
 * reported to it. That is frequently not this machine's, and matching on it alone is why a second
 * registration made every call-scoped command fail with "no matching session".
 *
 * So Home now resolves the call's legs and sends them all as `callIds`, and we match the one we
 * actually hold a session for - this client is the only party that knows which leg is its own.
 * Home keeps the set inside a single call, so the candidates can never span two of the user's
 * calls; picking the first that matches cannot land on the wrong one.
 *
 * `callIds` is absent when Home predates this or could not resolve the legs, and then this falls
 * back to `callId` and behaves exactly as it did before.
 *
 * @param {object} state redux state
 * @param {string} [callId] the anchor leg, as sent by the signalling surface
 * @param {string[]} [callIds] every leg of the same call, anchor first
 * @returns {{session: object|undefined, matchedCallId: string|undefined}}
 */
const findSessionForCommand = (state, callId, callIds) => {
    const candidates =
        Array.isArray(callIds) && callIds.length > 0 ? callIds : [callId];

    const matchedCallId = candidates.find(
        (candidate) => !!getLocalSessionFromCallId(state, candidate),
    );

    return {
        session: matchedCallId
            ? getLocalSessionFromCallId(state, matchedCallId)
            : undefined,
        matchedCallId,
    };
};

/**
 * Describe which leg was matched, for the log. Worth saying explicitly when it is not the leg the
 * command named: that is the double-registration case working, and it is otherwise invisible.
 */
/**
 * The legs the command offered, for the logs that fire when none of them match. Reads "none" rather
 * than empty when Home sent no set at all, because an unresolved lookup and a resolved one that
 * simply found nothing here are different failures and would otherwise look identical.
 */
const candidateLegs = (callIds) =>
    Array.isArray(callIds) && callIds.length > 0 ? callIds.join(', ') : 'none';

const describeMatch = (callId, matchedCallId) =>
    matchedCallId === callId
        ? `call ${callId}`
        : `call ${matchedCallId} (matched this client's leg; the command named ${callId})`;

const declineCommandedCall =
    (command, callId, callIds) => (dispatch, getState) => {
        const { session, matchedCallId } = findSessionForCommand(
            getState(),
            callId,
            callIds,
        );
        if (
            !session ||
            session.direction !== 'incoming' ||
            !session.isInProgress()
        ) {
            addUserPhoneLogEntry(
                `Companion command "${command}" for callId ${callId} (legs: ${candidateLegs(callIds)}): no matching ringing session, ignoring`,
            );
            return;
        }
        addUserPhoneLogEntry(
            `Companion command: declining ${describeMatch(callId, matchedCallId)}`,
        );
        dispatch(hangupSession(session));
    };

export const handleCompanionCallControl = (data) => (dispatch, getState) => {
    if (data?.command === 'ping') {
        return answerCompanionPing(data)(dispatch, getState);
    }
    if (data?.command === 'pong') {
        return handleCompanionPong(data)(dispatch, getState);
    }
    return handleCompanionCallControlCommand(data)(dispatch, getState);
};

/**
 * Received on the companion (the media-owning endpoint) as a typed TOP push
 * ({@code type: 'companion_call_control'}). A command maps to a local phone action, so the media
 * stays on this client. Only the install the user designated acts at all, and on top of that every
 * command de-dupes on nonce, so a fan-out/redelivery can't act twice.
 *
 * - `answer`, `hold`, `unhold`, `dtmf` and `transfer` act on an existing session, so they
 *   additionally require a call match - that is what stops a fan-out acting on the wrong call.
 *   See {@link findSessionForCommand} for why that is a match against a set of legs rather than
 *   against one id.
 * - `call` has no session yet (it creates one), so the call check must not apply to it.
 * - `mute`/`unmute` apply to this client's audio rather than one call, so they carry no callId.
 *
 * @param {{command: string, callId?: string, callIds?: string[], number?: string, digits?: string, deviceId?: string, nonce?: string}} data
 */
const handleCompanionCallControlCommand = (data) => (dispatch, getState) => {
    const {
        command,
        callId,
        callIds,
        number,
        digits,
        nonce,
        deviceId: targetDeviceId,
    } = data ?? {};

    // Gated as well as the sending side, so the switch does not depend on every sender behaving,
    // and gated on the designation as well as the feature: a machine the user has turned "Use as
    // Companion" off on must not answer calls or open its microphone.
    if (!isDesignatedCompanion(getState())) {
        addUserPhoneLogEntry(
            `Companion command "${command}" ignored, this client is not acting as the companion`,
        );
        return;
    }

    // Addressed to one machine: only that machine acts. The backend stamps the device it has
    // designated, so a client that still believes it is the companion - because the push saying
    // otherwise arrived while it was shut down - cannot answer a call that is no longer its to
    // take.
    //
    // Fails closed on both halves of the match, and the unaddressed half is not hypothetical: the
    // backend stamps whatever it has designated for the account, so a command carrying no device
    // is one sent for an account with no companion at all. Acting on it would let an install still
    // holding a previous user's `useAsCompanion` answer the current user's calls. Acting on a
    // command addressed elsewhere while not knowing our own id is the double-answer this addressing
    // exists to prevent. Neither is worth the one thing standing aside costs, a command ignored.
    const deviceId = getCompanionDeviceId();
    if (!targetDeviceId || targetDeviceId !== deviceId) {
        addUserPhoneLogEntry(
            `Companion command "${command}" ignored, addressed to device ${targetDeviceId ?? 'nobody'} (this device is ${deviceId ?? 'unknown'})`,
        );
        return;
    }

    if (alreadyHandled(nonce)) {
        addUserPhoneLogEntry(
            `Companion command ignored, duplicate nonce ${nonce}`,
        );
        return;
    }

    switch (command) {
        case 'answer': {
            const { session, matchedCallId } = findSessionForCommand(
                getState(),
                callId,
                callIds,
            );
            if (!session) {
                addUserPhoneLogEntry(
                    `Companion command "${command}" for callId ${callId} (legs: ${candidateLegs(callIds)}): no matching ringing session, ignoring`,
                );
                return;
            }
            addUserPhoneLogEntry(
                `Companion command: answering ${describeMatch(callId, matchedCallId)}`,
            );
            dispatch(answerCall(session));
            break;
        }
        case 'decline': {
            declineCommandedCall(command, callId, callIds)(dispatch, getState);
            break;
        }
        case 'call': {
            if (!number) {
                addUserPhoneLogEntry(
                    'Companion command "call" without a number, ignoring',
                );
                return;
            }
            addUserPhoneLogEntry(`Companion command: calling ${number}`);
            dispatch(startLocalCall(number));
            break;
        }
        case 'hold':
        case 'unhold': {
            const { session, matchedCallId } = findSessionForCommand(
                getState(),
                callId,
                callIds,
            );
            if (!session) {
                addUserPhoneLogEntry(
                    `Companion command "${command}" for callId ${callId} (legs: ${candidateLegs(callIds)}): no matching session, ignoring`,
                );
                return;
            }
            addUserPhoneLogEntry(
                `Companion command: ${command} ${describeMatch(callId, matchedCallId)}`,
            );
            dispatch(
                command === 'hold' ? holdCall(session) : unholdCall(session),
            );
            break;
        }
        case 'mute':
        case 'unmute': {
            // No callId: mute belongs to the media endpoint, not one call.
            addUserPhoneLogEntry(`Companion command: ${command}`);
            dispatch(mutePhone(command === 'mute'));
            break;
        }
        case 'dtmf': {
            if (!digits) {
                addUserPhoneLogEntry(
                    'Companion command "dtmf" without digits, ignoring',
                );
                return;
            }
            const { session, matchedCallId } = findSessionForCommand(
                getState(),
                callId,
                callIds,
            );
            if (!session) {
                addUserPhoneLogEntry(
                    `Companion command "dtmf" for callId ${callId} (legs: ${candidateLegs(callIds)}): no matching session, ignoring`,
                );
                return;
            }
            addUserPhoneLogEntry(
                `Companion command: sending DTMF ${digits} on ${describeMatch(callId, matchedCallId)}`,
            );
            // One dispatch with the whole batch, not one per character: sendDTMF passes the string
            // straight to RTCDTMFSender.insertDTMF, which applies its own duration and inter-tone
            // gap. Splitting it into a tight loop would emit the tones with no gap and some IVRs
            // would fail to recognise them.
            //
            // Addressed to the leg we matched rather than whatever is focused here: the user is
            // pressing keys on another machine, so this client's focus is no evidence of which
            // call they meant, and with two calls up the tones would land in the wrong one.
            //
            // No local feedback tone: the user pressed the key on the signalling surface, which
            // already played one. This machine is meant to be running in the background, and when
            // both are the same laptop the user otherwise hears every digit twice.
            dispatch(sendDTMF(digits, matchedCallId, false));
            break;
        }
        case 'transfer': {
            if (!number) {
                addUserPhoneLogEntry(
                    'Companion command "transfer" without a number, ignoring',
                );
                return;
            }
            const { session, matchedCallId } = findSessionForCommand(
                getState(),
                callId,
                callIds,
            );
            if (!session) {
                addUserPhoneLogEntry(
                    `Companion command "transfer" for callId ${callId} (legs: ${candidateLegs(callIds)}): no matching session, ignoring`,
                );
                return;
            }
            addUserPhoneLogEntry(
                `Companion command: blind transfer of ${describeMatch(callId, matchedCallId)} to ${number}`,
            );
            // Same reasoning as DTMF above: name the matched leg rather than trusting focus here.
            dispatch(
                transferBlindCall(number, 'companion command', matchedCallId),
            );
            break;
        }
        default:
            addUserPhoneLogEntry(
                `Companion command: unsupported command "${command}"`,
            );
    }
};

/**
 * Relay through Home: POST to `/companion/command`, which pushes it into every TOP in the region
 * until one of them has the companion connected. The original path, kept as the fallback for when
 * this client has no TOP socket of its own.
 *
 * Fire-and-forget: a 2xx means Home accepted and relayed it, not that the companion acted.
 */
/**
 * The device every command is addressed to: the one Home has designated for this account, read from
 * the fetched list at send time rather than from anything this surface stored.
 *
 * Read straight from the store because the senders are plain functions called from a component
 * callback and a DTMF debounce timer, neither of which carries a dispatch.
 *
 * Sent even though the current backend overrides it with the same value, because the deployed one
 * does not: routing by the account's designation is new alongside this change, and until it is out
 * everywhere Home relays a command to whichever device the command names. Sending nothing would
 * therefore address every command to nobody for as long as the two are out of step - and
 * handleCompanionCallControl now, correctly, ignores those. Removable once the backend change has
 * shipped everywhere.
 */
const addressedDeviceId = () => {
    // Required at call time rather than imported: the store module pulls in the reducers and
    // middleware behind it, and importing it here puts that whole graph behind anything that
    // imports phone-actions - including tests that have no reason to know about it. By the time a
    // command is sent the store exists, so there is nothing to gain from having it earlier.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { store } = require('flow/configure-store');
    const designated = selectDesignatedCompanionDevice(store.getState());
    return designated ? designated.get('deviceId') : null;
};

const sendCompanionCommandViaHome = (command, message) => {
    CompanionResource.sendCommand(
        {
            onResult() {},
            onClientError(error) {
                addUserPhoneLogEntry(
                    `Companion ${command} request rejected: ${error}`,
                );
            },
            onError(error) {
                addUserPhoneLogEntry(
                    `Companion ${command} request failed: ${error}`,
                );
            },
        },
        message,
    );
};

/**
 * Sent from the signalling surface (CTI) to this user's companion.
 *
 * Goes down this client's own TOP socket. TOP looks the companion up among the connections it
 * holds and hands it the same push envelope Home used to produce, so the receiving side is
 * unchanged - what disappears is an HTTPS round-trip to Home, a database lookup and a blocking
 * call back into TOP, all of them in the path between pressing Answer and the call being answered.
 *
 * Home stays as the fallback for the one case TOP cannot cover: no socket to send down. A command
 * that reaches TOP but times out is retried through Home too - the companion de-dupes on the
 * nonce, so arriving twice is harmless, whereas not arriving at all is the failure this is meant
 * to avoid.
 *
 * Authorization is unchanged in substance: Home resolved the target from the authenticated user,
 * and TOP takes it from the session's login state. Neither reads it from the command.
 *
 * @param {string} command see CompanionCommandDTO#getCommand
 * @param {object} [payload] command-specific fields (callId, number, digits, deviceId)
 */
const sendCompanionCommand = (command, payload = {}) => {
    const describe = Object.entries({
        ...payload,
        deviceId: addressedDeviceId(),
    })
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([key, value]) => `${key}=${value}`)
        .join(' ');
    addUserPhoneLogEntry(
        `Requesting companion to ${command}${describe ? ` (${describe})` : ''}`,
    );

    // After the spread, not before: a caller passing its own deviceId would otherwise overwrite
    // the designated one with whatever it happened to have, which for every current caller is null.
    const message = {
        command,
        nonce: generateCompanionNonce(),
        ...payload,
        deviceId: addressedDeviceId(),
    };

    if (!isConnected()) {
        addUserPhoneLogEntry(
            `No TOP connection, relaying companion ${command} via Home`,
        );
        sendCompanionCommandViaHome(command, message);
        return;
    }

    // Unaddressed commands go the long way round, because only Home can address them. TOP passes
    // through whatever device the sender names and the companion ignores anything not naming it,
    // so a command sent without one is dropped in silence - whereas Home resolves the account's
    // designated device for itself. The gap is the boot window: the device list arrives behind two
    // sequential fetches and answering is not gated on it, so a call ringing as the surface starts
    // can be answered before there is anything to address.
    if (!message.deviceId) {
        addUserPhoneLogEntry(
            `No companion device known yet, relaying ${command} via Home so it can address it`,
        );
        sendCompanionCommandViaHome(command, message);
        return;
    }

    companionCommand(message).catch((failure) => {
        if (failure?.reason === 'ERROR') {
            // TOP answered, and the answer was no - a malformed command, or no anumber on the
            // session. Home would refuse it for the same reasons, so there is nothing to retry.
            addUserPhoneLogEntry(
                `Companion ${command} request rejected by TOP: ${failure.response?.Message ?? 'no reason given'}`,
            );
            return;
        }

        addUserPhoneLogEntry(
            `Companion ${command} request got no answer from TOP, relaying via Home`,
        );
        sendCompanionCommandViaHome(command, message);
    });
};

export const answerCompanionCall = (callId) =>
    sendCompanionCommand('answer', { callId });

export const declineCompanionCall = (callId) =>
    sendCompanionCommand('decline', { callId });

/** Put the companion-owned call on hold / take it off hold. */
export const holdCompanionCall = (callId) =>
    sendCompanionCommand('hold', { callId });

export const unholdCompanionCall = (callId) =>
    sendCompanionCommand('unhold', { callId });

/**
 * Mute/unmute the companion's audio. Deliberately not addressed to a callId - mute is a property
 * of the media endpoint, which is exactly why it cannot be done from the CTI locally and needs
 * this channel at all.
 */
export const muteCompanion = (muted) =>
    sendCompanionCommand(muted ? 'mute' : 'unmute');

/**
 * Report this client's microphone state to TOP so the signalling surface can show it.
 *
 * Called from {@link mutePhone}, where the in-call button, a relayed command and the headset's own
 * button all converge - the headset reaches it through onMuteEvent in headset-callbacks.
 *
 * Reports the request rather than a session transition, so asking for a mute the endpoint is
 * already in still reports: a surface showing the opposite has nothing else to correct it.
 *
 * Only the designated companion reports - elsewhere the claim would be false.
 *
 * @param {boolean} muted
 * @param {object} [session] a local session, used only to name the channel
 */
export const publishCompanionMuteState =
    (muted, session) => (_dispatch, getState) => {
        const state = getState();

        if (!isDesignatedCompanion(state)) {
            return;
        }

        const callId = session?.data?.callId;
        // Without it TOP drops the report when two calls are up, so send it whenever we have it.
        const channel = callId ? getChannelNameForCallId(state, callId) : null;

        addUserPhoneLogEntry(
            `Reporting companion mute state ${muted} to TOP` +
                (channel ? ` for channel ${channel}` : ' (no channel known)'),
        );
        setCompanionMute(muted, channel);
    };

/** Send DTMF tones on the companion-owned call. */
export const sendCompanionDtmf = (digits, callId) =>
    sendCompanionCommand('dtmf', { callId, digits });

/**
 * Blind-transfer the companion-owned call to `number`.
 *
 * No control in the CTI reaches this yet - RemoteCall offers answer, hold and unhold - but the verb
 * is part of the relay on both sides, accepted by Home and by TOP, and covered by the transport
 * tests. Kept rather than trimmed as unreachable for that reason: the surface is the protocol's,
 * not this file's.
 */
export const transferCompanionCall = (number, callId) =>
    sendCompanionCommand('transfer', { callId, number });

/**
 * Sent from the signalling surface (CTI) when the user places an outbound call. The companion
 * dials from its own SIP user agent, so the media lands there and never in the CTI - the
 * mirror image of `answerCompanionCall`, over the same channel.
 *
 * Note this is deliberately not Home's `createActiveCall` ("call through Home") path: that rings
 * every SIP registration and relies on Alert-Info auto-answer to pick the media endpoint, which
 * cannot target the companion. Here the companion itself originates the INVITE.
 *
 * @param {string} number the number to dial
 */
export const callOnCompanion = (number) =>
    sendCompanionCommand('call', { number });
